// Share helpers for the referral ShareKit. Pure, framework-free functions so the
// URL-building logic can be unit-tested without rendering React.
//
// Why this matters: a referral is ONLY credited (and paid) when the referred
// person signs up by CLICKING the referrer's `?ref=` link. A typed name or
// word-of-mouth mention earns nothing. So every share path here carries the
// referrer's real tracked link — that's the whole point of giving referrers a
// ready-made, link-bearing message.

const enc = encodeURIComponent;

// Subject line used for the email share and the native-share title.
export const SHARE_TITLE = 'Earn £20 with your LinkedIn account';

// The prefilled, copy-ready message. Addressed to the person being referred (who
// must already have a LinkedIn account) and ends with the referrer's tracked
// link so a copy-paste or a share always carries it.
export function defaultTemplate(url) {
  return (
    "Hi! 👋 Do you have a LinkedIn account that's at least 6 months old?\n\n" +
    "If you don't use it, you can sell it for £20 — it's quick and you get " +
    'paid within 24 hours of completion. Sign up with my link here:\n' +
    (url || '')
  );
}

// Build a share-intent URL for a given network.
//   network — one of: whatsapp | facebook | x | telegram | email | sms
//   message — the full (possibly user-edited) copy; already contains `url`
//   url     — the bare referral link, for networks that take it as its own param
//
// Everything user-controlled is passed through encodeURIComponent, so the
// returned string is always a safe href (no markup/script can break out).
export function buildShareUrl(network, message, url) {
  const text = message || '';
  const link = url || '';
  switch (network) {
    case 'whatsapp':
      return `https://wa.me/?text=${enc(text)}`;
    case 'facebook':
      // Facebook's sharer only honours the URL — it builds the preview from the
      // destination page's Open Graph tags and strips any custom message.
      return `https://www.facebook.com/sharer/sharer.php?u=${enc(link)}`;
    case 'x':
      // X auto-links any URL inside the tweet text, so the link in `text` is
      // enough — no separate `url` param (which would duplicate it).
      return `https://twitter.com/intent/tweet?text=${enc(text)}`;
    case 'telegram': {
      // Telegram prepends the `url` param itself, so strip the link out of the
      // body text to avoid showing it twice.
      const body = link ? text.split(link).join('').replace(/\n+$/, '').trim() : text;
      return `https://t.me/share/url?url=${enc(link)}&text=${enc(body)}`;
    }
    case 'email':
      return `mailto:?subject=${enc(SHARE_TITLE)}&body=${enc(text)}`;
    case 'sms':
      // `sms:?&body=` is the most broadly compatible form across iOS + Android.
      return `sms:?&body=${enc(text)}`;
    default:
      return link;
  }
}
